import { useState } from "react";
import { Link, useNavigate } from "react-router";
import { useQueryClient } from "@tanstack/react-query";
import type { PlotPurchaseResponse, RealEstateListing } from "@townsy/contracts";
import { errorMessage } from "../../api/client";
import { queryKeys, useBootstrapQuery, usePropertiesQuery, useRealEstateListingsQuery, useWalletsQuery } from "../../api/queries";
import { plotCheckout } from "../../commerce/checkoutTargets";
import { useTownStore } from "../../state/store";
import { useDollarGate } from "../checkout/useDollarGate";
import { PurchaseDialog } from "./PurchaseDialog";
import { RealEstateListingsView, type WalletState } from "./RealEstatePanel";

/**
 * Townsy Real Estate inside the accessible `/directory` page and the no-3D fallback: the same
 * listings, the same coin purchase (same review dialog, same Idempotency-Key handling) and the same
 * dollar checkout as the office in the 3D town, so buying a plot never requires WebGL (docs/01 section 7).
 */
export function DirectoryRealEstate() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const boot = useBootstrapQuery();
  const isGuest = !boot.data?.session.userId;
  const listingsQ = useRealEstateListingsQuery(true);
  const walletsQ = useWalletsQuery(Boolean(boot.data) && !isGuest);
  const [buying, setBuying] = useState<RealEstateListing | null>(null);
  const dollar = useDollarGate();
  const openCheckout = useTownStore((s) => s.openCheckout);
  const [bought, setBought] = useState<{ id: string; address: string } | null>(null);

  const refresh = () => {
    void qc.invalidateQueries({ queryKey: queryKeys.realEstateListings });
    void qc.invalidateQueries({ queryKey: queryKeys.wallets });
    void qc.invalidateQueries({ queryKey: queryKeys.properties });
  };
  const walletState: WalletState = isGuest ? "none" : walletsQ.data ? "ready" : walletsQ.error ? "error" : "loading";
  const owned = listingsQ.data?.ownedPropertyIds ?? [];
  // Owned plots are no longer listed: their addresses come from the public property list (same query as the directory).
  const propertiesQ = usePropertiesQuery(owned.length > 0);
  const addressOf = (id: string) => propertiesQ.data?.items.find((p) => p.id === id)?.address ?? id;

  return (
    <section className="stack" aria-labelledby="directory-real-estate" id="real-estate" data-testid="directory-real-estate">
      <h2 id="directory-real-estate">Townsy Real Estate · 1 Plaza Way</h2>
      {bought ? (
        <div className="notice notice-success" role="status">
          You own {bought.address}. Your home starts as a tent. <Link to={`/place/${bought.id}`}>See it in the town</Link>.
        </div>
      ) : null}
      <RealEstateListingsView
        state={listingsQ.isPending ? "loading" : listingsQ.error ? "error" : "ready"}
        error={listingsQ.error ? errorMessage(listingsQ.error) : null}
        onRetry={() => void listingsQ.refetch()}
        data={listingsQ.data ?? null}
        isGuest={isGuest}
        wallets={walletsQ.data ?? null}
        walletState={walletState}
        ownedAddresses={owned.map(addressOf)}
        onGoToOwned={owned[0] ? () => navigate(`/place/${owned[0]}`) : null}
        onDetails={(id) => navigate(`/place/${id}`)}
        onBuy={setBuying}
        dollar={dollar}
        onBuyUsd={(listing) => openCheckout(plotCheckout(listing))}
      />
      {buying && walletsQ.data ? (
        <PurchaseDialog
          listing={buying}
          coinsAvailable={walletsQ.data.coins.available}
          onClose={() => setBuying(null)}
          onStale={refresh}
          onPurchased={(res: PlotPurchaseResponse) => {
            setBought({ id: res.propertyId, address: buying.address });
            setBuying(null);
            refresh();
            // The resident now has a home: Home Builders / Furnishings / My home on this page re-read it.
            void qc.invalidateQueries({ queryKey: queryKeys.myHome });
            void qc.invalidateQueries({ queryKey: queryKeys.inventory });
          }}
        />
      ) : null}
    </section>
  );
}
